import axios from 'axios';
import { toLocalISODate } from '../utils/dateUtils';
import { triggerSessionExpired } from '../utils/notify';

// Auto-detect API URL based on environment
const getApiUrl = () => {
  // Si hay variable de entorno, usarla
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // En desarrollo local (localhost:3000)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000/api';
  }
  
  // En producción (usa el mismo dominio)
  return '/api';
};

const API_URL = getApiUrl();

export const getApiBaseUrl = () => API_URL;

export const getBackendOrigin = () => API_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isLoginRequest = url.includes('/auth/login');
    const isLogoutRequest = url.includes('/auth/logout');
    if (status === 401 && !isLoginRequest && !isLogoutRequest && typeof window !== 'undefined') {
      if (!triggerSessionExpired()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  getActivityLog: (params) => api.get('/auth/activity-log', { params }),
};

// User Management API
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  // Admin sets another user's password: POST /users/:id/reset-password with
  // { new_password }. The user must change it on their next login.
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`)
};

// Employee API
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  uploadPhoto: (id, formData) => api.post(`/employees/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadProfilePicture: (id, formData) => api.post(`/employees/${id}/profile-picture`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDepartments: () => api.get('/employees/departments'),
  getPositions: () => api.get('/employees/positions'),
  getStats: () => api.get('/employees/stats/overview'),
  exportCSV: () => api.get('/employees/export/csv', { responseType: 'blob' }),
  getDeductions: (employeeId) => api.get(`/employees/${employeeId}/deductions`),
  getAvailableDeductions: (employeeId) => api.get(`/employees/${employeeId}/deductions/available`),
  addDeduction: (employeeId, data) => api.post(`/employees/${employeeId}/deductions`, data),
  updateDeduction: (employeeId, deductionId, data) => api.put(`/employees/${employeeId}/deductions/${deductionId}`, data),
  removeDeduction: (employeeId, deductionId) => api.delete(`/employees/${employeeId}/deductions/${deductionId}`),
  // the guard portal account (employees:update)
  getPortalAccess: (employeeId) => api.get(`/employees/${employeeId}/portal-access`),
  givePortalAccess: (employeeId) => api.post(`/employees/${employeeId}/portal-access`),
  revokePortalAccess: (employeeId) => api.delete(`/employees/${employeeId}/portal-access`)
};

// Attendance API
export const attendanceAPI = {
  getAll: (params) => api.get('/attendance', { params }),
  getById: (id) => api.get(`/attendance/${id}`),
  clockIn: (data) => api.post('/attendance/clock-in', data),
  create: (data) => api.post('/attendance', data),
  bulkCreate: (records) => api.post('/attendance/bulk', { records }),
  clockOut: (data) => api.put('/attendance/clock-out', data),
  // Time & Attendance > Today: active employees, today's records, open night shifts
  getToday: () => api.get('/attendance/today'),
  // Week tab: one week (any day of it; none = this week), "Mark as..." and copying a week
  getWeek: (start) => api.get('/attendance/week', { params: start ? { start } : {} }),
  markDays: (data) => api.post('/attendance/mark', data),
  copyWeek: (data) => api.post('/attendance/copy-week', data),
  // Side panel: what payroll would pay for the day being edited (nothing saved)
  previewDay: (data) => api.post('/attendance/day-preview', data),
  // Employee tab: { employee_id, month: 'YYYY-MM' }
  getMonth: (params) => api.get('/attendance/month', { params }),
  createManual: (data) => api.post('/attendance/manual', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`),
  getEmployeeAttendance: (employeeId, params) => api.get(`/attendance/employee/${employeeId}`, { params }),
  getByDateRange: (params) => api.get('/attendance', { params }),
  getTodayAttendance: () => {
    const today = toLocalISODate(new Date());
    return api.get('/attendance', { params: { start_date: today, end_date: today } });
  },
  getStatistics: (params) => api.get('/attendance/statistics', { params }),
  getSummary: (params) => api.get('/attendance/summary', { params }),
  getMonthlyReport: (month, year) => api.get('/attendance/monthly-report', { params: { month, year } }),
  getDepartments: () => api.get('/attendance/departments'),
  approve: (id, data) => api.post(`/attendance/${id}/approve`, data),
  reject: (id, data) => api.post(`/attendance/${id}/reject`, data),
};

// Leave API
export const leaveAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  create: (data) => api.post('/leaves/request', data),
  update: (id, data) => api.put(`/leaves/${id}`, data),
  delete: (id) => api.delete(`/leaves/${id}`),
  approve: (id, data) => api.post(`/leaves/${id}/approve`, data),
  reject: (id, data) => api.post(`/leaves/${id}/reject`, data),
  getTypes: () => api.get('/leaves/types'),
  getBalance: (employeeId) => api.get(`/leaves/balance/${employeeId}`),
  // Time & Attendance > Leave: { status, employee_id, leave_type_id }
  getBoard: (params) => api.get('/leaves/board', { params }),
  // The menu's counter of leave waiting for a decision
  getPendingCount: () => api.get('/leaves/pending-count'),
  getBalances: (employeeId) => api.get(`/leaves/balances/${employeeId}`)
};

// Overtime API
export const overtimeAPI = {
  getConfigurations: () => api.get('/overtime/configurations'),
  createConfiguration: (data) => api.post('/overtime/configurations', data),
  updateConfiguration: (id, data) => api.put(`/overtime/configurations/${id}`, data),
  deleteConfiguration: (id) => api.delete(`/overtime/configurations/${id}`),
  toggleConfiguration: (id) => api.patch(`/overtime/configurations/${id}/toggle`),
  // Overtime tax is frozen: what payroll applies, read-only.
  getAppliedTax: () => api.get('/overtime/tax-applied')
};

// Payroll API
export const payrollAPI = {
  getAll: (params) => api.get('/payroll', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  calculate: (data) => api.post('/payroll/calculate', data),
  approve: (id, data) => api.post(`/payroll/${id}/approve`, data),
  reject: (id, data) => api.post(`/payroll/${id}/reject`, data),
  delete: (id) => api.delete(`/payroll/${id}`),
  getSummary: (params) => api.get('/payroll/summary', { params }),
  preview: (data) => api.post('/payroll/preview', data),
};

// Finance API
export const financeAPI = {
  getExpenses: (params) => api.get('/finance/expenses', { params }),
  createExpense: (data) => api.post('/finance/expenses', data),
  updateExpense: (id, data) => api.put(`/finance/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/finance/expenses/${id}`),
  approveExpense: (id, data) => api.post(`/finance/expenses/${id}/approve`, data),
  rejectExpense: (id, data) => api.post(`/finance/expenses/${id}/reject`, data),
  getRevenues: (params) => api.get('/finance/revenues', { params }),
  createRevenue: (data) => api.post('/finance/revenues', data),
  updateRevenue: (id, data) => api.put(`/finance/revenues/${id}`, data),
  deleteRevenue: (id) => api.delete(`/finance/revenues/${id}`),
  getCashOnHand: (params) => api.get('/finance/cash-on-hand', { params }),
  setOpeningBalance: (data) => api.put('/finance/opening-balance', data),
  getSummary: (params) => api.get('/finance/summary', { params }),
  uploadReceipt: (id, formData) => api.post(`/finance/expenses/${id}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadReceipt: (id) => api.get(`/finance/expenses/${id}/receipt`, { responseType: 'blob' }),
  getBudgets: (params) => api.get('/finance/budgets', { params }),
  createBudget: (data) => api.post('/finance/budgets', data),
  updateBudget: (id, data) => api.put(`/finance/budgets/${id}`, data),
  deleteBudget: (id) => api.delete(`/finance/budgets/${id}`),
  getBudgetComparison: (params) => api.get('/finance/budget-comparison', { params }),
  getTrends: (params) => api.get('/finance/trends', { params }),
  getBurnRate: (params) => api.get('/finance/burn-rate', { params }),
  getRunway: (params) => api.get('/finance/runway', { params })
};

// Benefits API
export const benefitsAPI = {
  getTypes: () => api.get('/benefits/types'),
  createType: (data) => api.post('/benefits/types', data),
  updateType: (id, data) => api.put(`/benefits/types/${id}`, data),
  deleteType: (id) => api.delete(`/benefits/types/${id}`),
  getEmployeeBenefits: (params) => api.get('/benefits', { params }),
  assignBenefit: (data) => api.post('/benefits', data),
  updateEmployeeBenefit: (id, data) => api.put(`/benefits/${id}`, data),
  cancelEmployeeBenefit: (id) => api.post(`/benefits/${id}/cancel`),
  deleteEmployeeBenefit: (id) => api.delete(`/benefits/${id}`),
};

// Documents API
export const documentsAPI = {
  getCategories: () => api.get('/documents/categories'),
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`)
};

// Contracts API
// Contracts API
export const contractsAPI = {
  // Templates
  getTemplates: (params) => api.get('/contracts/templates', { params }),
  getTemplate: (id) => api.get(`/contracts/templates/${id}`),
  createTemplate: (data) => api.post('/contracts/templates', data),
  updateTemplate: (id, data) => api.put(`/contracts/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/contracts/templates/${id}`),
  
  // Contracts
  getAll: (params) => api.get('/contracts', { params }),
  getById: (id) => api.get(`/contracts/${id}`),
  getEmployeeForContract: (employeeId) => api.get(`/contracts/employee/${employeeId}`), // ← AGREGAR ESTA LÍNEA
  create: (data) => api.post('/contracts', data),
  createFromTemplate: (data) => api.post('/contracts/from-template', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  sign: (id, data) => api.post(`/contracts/${id}/sign`, data),
  activate: (id) => api.post(`/contracts/${id}/activate`),
  terminate: (id, data) => api.post(`/contracts/${id}/terminate`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  generatePDF: (id) => api.get(`/contracts/${id}/generate`),
  getStats: () => api.get('/contracts/stats') // ← TAMBIÉN FALTABA ESTE
};
// Tax Configuration API - Updated with brackets support
export const taxConfigAPI = {
  getAll: (params) => api.get('/tax-config', { params }),
  getById: (id) => api.get(`/tax-config/${id}`),
  // Tax brackets functions
  // Brackets belong to a year (payroll uses the year the period ends in).
  getBrackets: (id, year) => api.get(`/tax-config/${id}/brackets`, { params: { year } }),
  updateBrackets: (id, data) => api.put(`/tax-config/${id}/brackets`, data),
  // Tax management functions
  create: (data) => api.post('/tax-config', data),
  update: (id, data) => api.put(`/tax-config/${id}`, data),
  delete: (id) => api.delete(`/tax-config/${id}`),
  toggleStatus: (id) => api.patch(`/tax-config/${id}/toggle`)
};

// Deduction API
export const deductionAPI = {
  getAll: () => api.get('/deductions'),
  create: (data) => api.post('/deductions', data),
  update: (id, data) => api.put(`/deductions/${id}`, data),
  delete: (id) => api.delete(`/deductions/${id}`)
};

// Approvals API
export const approvalAPI = {
  getAll: (params) => api.get('/approvals', { params }),
  getPending: () => api.get('/approvals/pending'),
  getHistory: () => api.get('/approvals/history'),
  getMyRequests: () => api.get('/approvals/my-requests'),
  approve: (id, data) => api.post(`/approvals/${id}/approve`, data),
  reject: (id, data) => api.post(`/approvals/${id}/reject`, data)
};

// Permissions API
export const permissionsAPI = {
  getAll: () => api.get('/permissions'),
  bulkUpdate: (permissions) => api.put('/permissions/bulk', { permissions }),
  getUserPermissions: (userId) => api.get(`/permissions/user/${userId}`),
  updateUserPermissions: (userId, permissions) => api.put(`/permissions/user/${userId}`, { permissions }),
  getRolePermissions: (role) => api.get(`/permissions/role/${role}`),
  updateRolePermissions: (role, permissions) => api.put(`/permissions/role/${role}`, { permissions })
};

// Settings API
// Settings API - VERSIÓN CORREGIDA Y UNIFICADA
export const settingsAPI = {
  getAll: (category) => api.get('/settings', { params: { category } }),
  getByKey: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { setting_value: value }),
  
  bulkUpdate: (settingsData) => {
    const settingsArray = Object.entries(settingsData).map(([key, value]) => ({
      key: key,
      value: value
    }));
    return api.put('/settings/bulk', { settings: settingsArray });
  },
  
  // Logs: URL corregida y consistente
  getLogs: (filters = {}) => {
    return api.get('/settings/activity-logs', { params: filters });
  },
  
  // Statistics: Unifica en una sola (elimina duplicados)
  getStatistics: () => api.get('/settings/statistics'),
  // payroll_settings: what payroll reads (premiums, working days, hours, sick pay)
  getPayrollSettings: () => api.get('/settings/payroll'),
  updatePayrollSettings: (config) => api.put('/settings/payroll', config),
  
  // Opcional: Alias para compatibilidad vieja (si usas en otro lugar)
  // getSystemStats: () => api.get('/settings/statistics'),
  // getStats: () => api.get('/settings/statistics'),
};
// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getSummary: () => api.get('/dashboard/summary'),
  getAttendanceTrend: () => api.get('/dashboard/attendance-trend'),
  getPayrollTrend: () => api.get('/dashboard/payroll-trend'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
  getActivities: () => api.get('/dashboard/recent-activity'),
  getDepartmentStats: () => api.get('/dashboard/department-stats'),
  getTopPerformers: (limit) => api.get('/dashboard/top-performers', { params: { limit } }),
};
// Public Holidays API
export const publicHolidaysAPI = {
  getAll: (params) => api.get('/public-holidays', { params }),
  getById: (id) => api.get(`/public-holidays/${id}`),
  create: (data) => api.post('/public-holidays', data),
  update: (id, data) => api.put(`/public-holidays/${id}`, data),
  delete: (id) => api.delete(`/public-holidays/${id}`),
  checkDate: (date) => api.get(`/public-holidays/check/${date}`)
};
// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteAllRead: () => api.delete('/notifications/read/clear'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  testNotification: (data) => api.post('/notifications/test', data)
};







// Clients and their sites (posts), staff side
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
};

export const sitesAPI = {
  list: (params) => api.get('/sites', { params }),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
};

// The shift planner: times are Paramaribo wall-clock 'YYYY-MM-DDTHH:mm'
export const shiftsAPI = {
  list: (params) => api.get('/shifts', { params }),
  guards: () => api.get('/shifts/guards'),
  sites: () => api.get('/shifts/sites'),
  check: (data) => api.post('/shifts/check', data),
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  cancel: (id) => api.post(`/shifts/${id}/cancel`),
  copyWeek: (data) => api.post('/shifts/copy-week', data),
};

export const shiftTemplatesAPI = {
  list: (params) => api.get('/shift-templates', { params }),
  create: (data) => api.post('/shift-templates', data),
  update: (id, data) => api.put(`/shift-templates/${id}`, data),
};

// Which optional modules this installation has on (FEATURES on the server)
export const featuresAPI = {
  get: () => api.get('/features'),
};

// The guard portal (role employee): always the signed-in guard's own data
export const portalAPI = {
  me: () => api.get('/portal/me'),
  shifts: () => api.get('/portal/shifts'),
  attendance: (month) => api.get('/portal/attendance', { params: { month } }),
  payslips: () => api.get('/portal/payslips'),
  payslip: (id) => api.get(`/portal/payslips/${id}`),
  leave: () => api.get('/portal/leave'),
  leaveTypes: () => api.get('/portal/leave/types'),
  leaveBalance: (year) => api.get('/portal/leave/balance', { params: { year } }),
  requestLeave: (data) => api.post('/portal/leave', data),
  // gps_checkin only
  consent: () => api.get('/portal/consent'),
  acceptConsent: (data) => api.post('/portal/consent', data),
  withdrawConsent: () => api.post('/portal/consent/withdraw'),
  report: (shiftId, data) => api.post(`/portal/shifts/${shiftId}/checkins`, data),
};

export default api;